<template lang="pug">
  .guided-voicemail(
    :data-id="screen.id"
    :data-voicemail="screen.voicemail"
    ref="elRef"
  )
    .guided-voicemail_container
      .guided-voicemail_main.flex.flex-col.items-center.my-4.md_flex-row.gap-6(
        :class="mainVoicemailDivClass"
      )
        .guided-voicemail_transcript.flex.items-start.justify-center.w-full.text-gray-500.dark_text-gray-50.prose-stack.overflow-hidden.overflow-y-auto.simple-scrollbar(
          ref="transcriptRef"
          :class="[screen['center-transcript'] ? 'text-center' : 'text-start', transcriptOrder, {'fade-bottom': fadeBottom }, {'fade-top': fadeTop }]"
          class="md_max-w-[50%]  max-h-[45vh]"
          v-if="!wordDoc && !audioDoc"
        )
          uc-text.guided-voicemail__transcript-text.h-full.px-6.simple-scrollbar(
            :k="`${screen.id}.transcript`"
            :text="screen.transcript"
            :class="{'md_max-w-[80%]': hasSlides}"
          )
        .guided-voicemail_transcript.flex.items-start.justify-center.w-full.text-gray-500.dark_text-gray-50.prose-stack(
          v-else-if="wordDoc || audioDoc"
        )
          uc-text.guided-voicemail__transcript-text.h-full.px-6.simple-scrollbar(
            :k="`${screen.id}.transcript`"
            :text="screen.transcript"
          )
        .guided-voicemail_slides.flex.items-center.justify-center.w-full(
          v-if="hasSlides"
          :class="transcriptOrder === 'order-1' ? 'order-2' : 'order-1'"
        )
          .guided-voicemail_slide(
            :id="getSlideId(i)"
            key="i"
            v-for="(s, i) in screen.slides"
          )
            .voicemail-slide_container.justify-center.w-full
              uc-screen.justify-center.w-full.py-2(
                :show-nested-bg="Boolean(s.style || s.bg)"
                :edit-path="`${editPath}.slides.${i}`"
                :screen="{ 'no-fade': true, ...s }"
              )

      .guided-voicemail_audio.flex.items-center.justify-between.md_justify-start.gap-3.w-full.rounded-xl.p-4.transition-colors(
        :class="playerClasses"
        v-if="!wordDoc && !audioDoc"
      )
        //- Hidden audio element
        .guided-voicemail__audioRef.hidden
          audio(
            @ended="onEnded"
            @play="playing = true"
            @timeupdate="onTimeUpdate"
            @loadedmetadata="onLoadedMetadata"
            @durationchange="onDurationChange"
            @error="onAudioError"
            crossOrigin="anonymous"
            ref="audioRef"
          )
            source(:key="audioSrc" :src="audioSrc" type="audio/mpeg")
        
        //- Rewind button
        button.focus-visible_focus-ring-8.guided-voicemail__rewind.guided-voicemail__control-btn.w-12.h-12.rounded-full.flex.items-center.justify-center.transition-all.ripple.flex-shrink-0(
          @click="rewindVoicemail"
          type="button"
          aria-label="Rewind 10 seconds"
        )
          uc-icon(icon="material-symbols:replay-10" class="text-2xl")
        
        //- Play/Pause button
        button.focus-visible_focus-ring-8.guided-voicemail__play-pause.guided-voicemail__control-btn.w-12.h-12.rounded-full.flex.items-center.justify-center.transition-all.ripple.flex-shrink-0(
          @click="toggleVoicemail"
          type="button"
          :aria-label="playing ? 'Pause' : 'Play'"
        )
          uc-icon(v-if="!playing" icon="carbon:play-filled-alt" class="text-2xl")
          uc-icon(v-else icon="carbon:pause-filled" class="text-2xl")
        
        //- Forward button
        button.focus-visible_focus-ring-8.guided-voicemail__forward.guided-voicemail__control-btn.w-12.h-12.rounded-full.flex.items-center.justify-center.transition-all.ripple.flex-shrink-0(
          @click="forwardVoicemail"
          type="button"
          aria-label="Forward 10 seconds"
        )
          uc-icon(icon="material-symbols:forward-10" class="text-2xl")
        
        //- Current time
        .guided-voicemail__time-current.text-sm.font-medium.tabular-nums.whitespace-nowrap.hidden.md_block {{ formatTime(currentTime) }}
        
        //- Progress bar (takes remaining space)
        .guided-voicemail__progress-container.flex-grow.h-1.rounded-full.cursor-pointer.relative.group.hidden.md_block(
          ref="seekRef"
        )
          .guided-voicemail__progress-fill.absolute.left-0.top-0.h-full.w-full.rounded-full.transition-colors.bg-primary-500(
            ref="seekProgressRef"
          )
        
        //- Total duration
        .guided-voicemail__time-duration.text-sm.font-medium.tabular-nums.whitespace-nowrap.hidden.md_block {{ formatTime(duration) }}
        
        //- Speed control
        .guided-voicemail__speed.relative.flex-shrink-0.hidden.md_block(
          ref="speedControlRef"
        )
          button.focus-visible_focus-ring-8.guided-voicemail__speed-btn.text-sm.font-medium.px-2.py-1.transition-all.whitespace-nowrap.flex.items-center.justify-center.hover_opacity-70(
            @click="toggleSpeedMenu"
            type="button"
            :aria-label="`Playback speed ${playbackRate}x`"
            :aria-expanded="showSpeedMenu"
            aria-haspopup="true"
          ) {{ playbackRate }}x
          
          .guided-voicemail__speed-menu.absolute.bottom-full.right-0.mb-2.rounded-lg.shadow-lg.overflow-hidden.transition-all.bg-gray-800.dark_bg-gray-100.border.border-gray-700.dark_border-gray-300(
            v-show="showSpeedMenu"
            role="menu"
          )
            button.block.w-full.text-left.px-4.py-2.text-sm.transition-colors.whitespace-nowrap.focus-visible_focus-ring-8(
              v-for="speed in playbackSpeeds"
              :key="speed"
              @click="setPlaybackSpeed(speed)"
              :class="speedMenuItemClass(speed)"
              type="button"
              role="menuitemradio"
              :aria-checked="playbackRate === speed"
              :aria-label="`Playback speed ${speed}x`"
            ) {{ speed }}x
        
        //- Mute button
        button.focus-visible_focus-ring-8.guided-voicemail__mute.w-10.h-10.flex.items-center.justify-center.transition-all.flex-shrink-0.hover_opacity-70(
          @click="toggleMute"
          type="button"
          :aria-label="localMuted ? 'Unmute' : 'Mute'"
        )
          uc-icon(v-if="!localMuted" icon="mdi:volume-high" class="text-lg")
          uc-icon(v-else icon="mdi:volume-off" class="text-lg")
</template>

<script lang="ts" setup>
import { stop } from '../lib/audio';
import gsap from 'gsap';
import { setCompleted } from '../lib/SCORM';
import {
  onUnmounted,
  onMounted,
  ref,
  watch,
  inject,
  unref,
  computed,
  nextTick
} from 'vue';
import {
  useEventListener,
  breakpointsTailwind,
  useBreakpoints
} from '@vueuse/core';
import bugAlert from '../lib/bugAlert';
import useSuspend from '../lib/useSuspend';
import expandUploadPath from '../lib/expandUploadPath';
import useSeek from '../lib/useSeek';

const suspend = useSuspend();
const props = defineProps<{
  screen: GuidedVoicemailScreen;
  editPath: string;
}>();

type GuidedVoicemailScreen = UcScreen & {
  'dont-unlock'?: boolean;
  id: string;
  voicemail: string;
  headshot: string;
  transcript: string;
  slides?: any[];
  'center-transcript'?: boolean;
  'transcript-position'?: 'left' | 'center' | 'right';
  question?: any;
  dark?: boolean;
  'player-styles'?: string;
};

const audioRef = ref<HTMLAudioElement>();
const transcriptRef = ref<HTMLDivElement>();
const seekRef = ref<HTMLDivElement>();
const seekProgressRef = ref<HTMLDivElement>();
const speedControlRef = ref<HTMLDivElement>();
const currentTime = ref(0);
const duration = ref(0);
const playbackRate = ref(1);
const showSpeedMenu = ref(false);
const playbackSpeeds = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const localMuted = ref(false);

const sectionId = inject('sectionId') as string;
const sectionActive = inject('sectionActive');
const breakpoints = useBreakpoints(breakpointsTailwind);
const smallerThanMediumDevice = breakpoints.smallerOrEqual('md');
const hasSlides = computed(() => {
  return props.screen.slides && props.screen.slides.length > 0;
});

const mainVoicemailDivClass = computed(() => {
  if (!props.screen['transcript-position']) return 'justify-center';
  return props.screen['transcript-position'] === 'center'
    ? 'justify-center'
    : props.screen['transcript-position'] === 'left'
      ? 'justify-start'
      : 'justify-end';
});

const transcriptOrder = computed(() => {
  // on small devices, the transcript should be below the slides
  return props.screen['transcript-position'] === 'left' &&
    !smallerThanMediumDevice.value
    ? 'order-1'
    : 'order-2';
});

const playerClasses = computed(() => {
  return props.screen['player-styles']
    ? props.screen['player-styles']
    : 'bg-gray-800 text-white dark_bg-gray-100 dark_text-gray-800';
});

const speedMenuItemClass = (speed: number) => {
  const isActive = playbackRate.value === speed;
  return `hover_bg-gray-700 dark_hover_bg-gray-200 text-gray-100 dark_text-gray-800 ${isActive ? 'font-bold' : ''}`;
};

// Track if transcript is near the bottom (within 20px)
const isNearBottom = ref(false);
// Track if transcript can scroll
const transcriptCanScroll = ref(false);

// Update isNearBottom and transcriptCanScroll based on scroll position
const updateScrollPosition = () => {
  const transcriptEl = unref(transcriptRef);
  if (!transcriptEl) {
    isNearBottom.value = false;
    transcriptCanScroll.value = false;
    return;
  }

  const { scrollTop, scrollHeight, clientHeight } = transcriptEl;
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
  isNearBottom.value = distanceFromBottom <= 20;
  transcriptCanScroll.value = scrollHeight > clientHeight;
};

const fadeBottom = computed(() => {
  return transcriptCanScroll.value && !isNearBottom.value;
});

const fadeTop = computed(() => {
  return transcriptCanScroll.value && isNearBottom.value;
});

const getSlideId = (i: number) => `${props.screen.id}_slide-${i}`;

watch(
  () => suspend.locale,
  locale => {
    hasTriedFallback.value = false; // Reset fallback flag on locale change
    if (unref(audioRef)) {
      resetVoicemail();
      updateProgress(0);
    }

    tl = null;
    initTranscriptAnim();
  }
);

watch(
  () => suspend.mute,
  muted => {
    const audio = unref(audioRef);

    if (audio) {
      audio.muted = muted;
      localMuted.value = muted;

      gsap.to(audio, {
        volume: muted ? 0 : 1
      });
    }
  }
);

watch(
  () => suspend.reducedMotion,
  reducedMotion => {
    if (reducedMotion) {
      tl?.pause();
      if (playing.value) {
        completeScreen();
      }
    } else {
      initTranscriptAnim();
    }
  }
);

if (!props.screen.voicemail) {
  bugAlert(`Voicemail missing in #${sectionId}`);
}

// Listen to scroll events on the transcript element
useEventListener(transcriptRef, 'scroll', updateScrollPosition);

// Initialize seek functionality for draggable progress bar
useSeek(seekRef, updateProgress);

// Close speed menu when clicking outside
useEventListener(document, 'click', (e: MouseEvent) => {
  const speedControl = unref(speedControlRef);
  if (speedControl && !speedControl.contains(e.target as Node)) {
    showSpeedMenu.value = false;
  }
});

// Show speed menu when keyboard navigating to it
useEventListener(speedControlRef, 'focusin', (e: FocusEvent) => {
  const target = e.target as HTMLElement;
  if (target?.closest('.guided-voicemail__speed-menu')) {
    showSpeedMenu.value = true;
  }
});

const ended = ref(false);
const playing = ref(false);
const audioDoc = inject('audioDoc') as boolean;
const wordDoc = inject('wordDoc') as Ref<boolean>;
const elRef = ref<HTMLElement | null>(null);

const getAudioSrcPath = (basePath: string, locale: string): string => {
  if (locale !== 'en-GB') {
    const pathWithoutLocale = basePath.replace(
      /_[a-z]{2}-[A-Z]{2}\.mp3$/,
      '.mp3'
    );
    return pathWithoutLocale.replace(/\.mp3$/, `_${locale}.mp3`);
  }
  return basePath.replace(/_[a-z]{2}-[A-Z]{2}\.mp3$/, '.mp3');
};

const baseAudioPath = expandUploadPath(props.screen.voicemail);
const audioSrc = ref('');
const hasTriedFallback = ref(false);

onMounted(() => {
  audioSrc.value = getAudioSrcPath(baseAudioPath, suspend.locale);
});

let tl: gsap.core.Timeline | null = null;

const shorten = 0.875;

const initTranscriptAnim = () => {
  console.log(
    'initTranscriptAnim',
    wordDoc.value,
    audioDoc,
    suspend.reducedMotion
  );
  if (wordDoc.value || audioDoc || suspend.reducedMotion) return;

  const transcriptEl = unref(transcriptRef);
  if (!transcriptEl) return;

  // Kill existing timeline if it exists
  if (tl) {
    tl.kill();
  }

  // Calculate scrollable distance
  const scrollHeight = transcriptEl.scrollHeight;
  const clientHeight = transcriptEl.clientHeight;
  const maxScroll = Math.max(0, scrollHeight - clientHeight);

  // Only create timeline if there's content to scroll
  if (maxScroll <= 0) {
    tl = null;
    return;
  }

  // Reset scroll position
  transcriptEl.scrollTop = 0;
  updateScrollPosition();

  // Create timeline that scrolls the transcript as audio plays
  tl = gsap
    .timeline({
      paused: true,
      onUpdate: () => {
        updateScrollPosition();
      }
    })
    .to(transcriptEl, {
      scrollTop: maxScroll,
      ease: 'none',
      duration: 1,
      autoRound: false
    });
};

let resizeObserver: ResizeObserver | null = null;

watch(audioRef, audio => {
  if (audio) {
    audio.muted = suspend.mute;
    localMuted.value = audio.muted;
  }
});

watch(transcriptRef, newEl => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  if (newEl) {
    resizeObserver = new ResizeObserver(() => {
      nextTick(() => {
        updateScrollPosition();
      });
    });

    // Observe the transcript element
    resizeObserver.observe(newEl);

    // Also observe the content inside (uc-text component) - try immediately and after nextTick
    const observeContent = () => {
      const contentEl = newEl.querySelector(
        '.guided-voicemail__transcript-text'
      );
      if (contentEl && resizeObserver) {
        resizeObserver.observe(contentEl);
      }
    };

    observeContent();
    nextTick(observeContent);
  }

  initTranscriptAnim();
  // Initialize scroll position when transcript element becomes available
  nextTick(() => {
    updateScrollPosition();
  });
});

const onAudioError = () => {
  const audio = unref(audioRef);
  if (!audio || hasTriedFallback.value) return;

  // If locale-specific file failed and we haven't tried fallback yet
  if (suspend.locale !== 'en-GB' && audioSrc.value !== baseAudioPath) {
    console.warn(
      `Locale-specific audio not found: ${audioSrc.value}, falling back to base file`
    );
    hasTriedFallback.value = true;
    audioSrc.value = baseAudioPath;
    // Reset the audio element to try loading the base file
    audio.load();
  }
};

const onEnded = () => {
  tl?.pause();
  // Ensure we're scrolled to the bottom
  const transcriptEl = unref(transcriptRef);
  if (transcriptEl && tl) {
    transcriptEl.scrollTop =
      transcriptEl.scrollHeight - transcriptEl.clientHeight;
    updateScrollPosition();
  }
  ended.value = true;
  completeScreen();
};

function completeScreen() {
  if (props.screen.question) return;
  if (props.screen['dont-unlock']) return;

  setCompleted(props.screen.id);
  setCompleted(sectionId);
}

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Update seek UI with GSAP animation
function updateSeekUI(
  progress: number,
  { duration = 0 }: { duration?: number } = {}
) {
  if (!seekProgressRef.value) return;

  gsap.to(seekProgressRef.value, {
    xPercent: progress * 100 - 100,
    duration
  });
}

// Initialize progress bar position when ref is available
watch(seekProgressRef, newRef => {
  if (newRef) {
    gsap.set(newRef, { xPercent: -100 });
  }
});

// Audio event handlers
function onTimeUpdate() {
  const audio = unref(audioRef);
  if (!audio) return;

  currentTime.value = audio.currentTime;
  const progress = getProgress();
  updateSeekUI(progress);
}

function onLoadedMetadata() {
  const audio = unref(audioRef);
  if (!audio) return;

  duration.value = audio.duration;
}

function onDurationChange() {
  const audio = unref(audioRef);
  if (!audio) return;

  duration.value = audio.duration;
}

// @ts-ignore - used in pug mixin
async function toggleVoicemail() {
  const audio = unref(audioRef);
  if (!audio) return;

  if (playing.value) {
    audio.pause();
  } else {
    audio.volume = 1;
    try {
      await audio.play();
    } catch (error) {
      console.warn('Audio play failed', error);
      completeScreen();
    }
  }
}

// Skip forward 10 seconds
function forwardVoicemail() {
  const audio = unref(audioRef);
  if (!audio) return;

  if (audio.duration)
    audio.currentTime = Math.min(audio.currentTime + 10, audio.duration);
}

// Skip backward 10 seconds
function rewindVoicemail() {
  const audio = unref(audioRef);
  if (!audio) return;

  audio.currentTime = Math.max(audio.currentTime - 10, 0);
}

// Toggle speed menu
function toggleSpeedMenu() {
  showSpeedMenu.value = !showSpeedMenu.value;
}

// Set playback speed
function setPlaybackSpeed(speed: number) {
  const audio = unref(audioRef);
  if (!audio) return;

  playbackRate.value = speed;
  audio.playbackRate = speed;
  showSpeedMenu.value = false;
}

function toggleMute() {
  const audio = unref(audioRef);
  if (!audio) return;

  audio.muted = !audio.muted;
  localMuted.value = !localMuted.value;

  gsap.to(audio, {
    volume: audio.muted ? 0 : 1
  });
}

function resetVoicemail() {
  const audio = unref(audioRef);
  if (!audio) return;

  audioSrc.value = getAudioSrcPath(baseAudioPath, suspend.locale);

  pause();
  audio.currentTime = 0;
  // Reset scroll position
  const transcriptEl = unref(transcriptRef);
  if (transcriptEl) {
    transcriptEl.scrollTop = 0;
    updateScrollPosition();
  }
  if (tl) {
    tl.progress(0);
  }
  updateProgress();
}

useEventListener(audioRef, 'play', () => {
  const audio = unref(audioRef);
  if (!audio) return;

  if (!tl) {
    initTranscriptAnim();
  }

  if (tl) {
    if (ended.value) {
      tl.progress(0);
      const transcriptEl = unref(transcriptRef);
      if (transcriptEl) {
        transcriptEl.scrollTop = 0;
        updateScrollPosition();
      }
      ended.value = false;
    }

    tl.duration(audio.duration * shorten);
    audio.volume = 1;
    gsap.set(audio, { volume: audio.volume });
    tl.play();
  }

  playing.value = true;

  // Stop screen content audio
  stop();
});

const pause = () => {
  const audio = unref(audioRef);
  if (!audio) return;

  tl?.pause();
  audio.pause();
  playing.value = false;
};

useEventListener(audioRef, 'pause', pause);

// Sync timeline progress with audio progress (handles seeking)
useEventListener(audioRef, 'timeupdate', () => {
  const audio = unref(audioRef);
  if (!audio || !tl || !audio.duration) return;

  const audioProgress = audio.currentTime / audio.duration;
  const timelineProgress = Math.min(audioProgress / shorten, 1);

  if (Math.abs(tl.progress() - timelineProgress) > 0.005) {
    tl.progress(timelineProgress);
  }
});

function getProgress() {
  const audio = unref(audioRef);
  if (!audio) return 0;

  const { currentTime, duration } = audio;
  if (!duration) return 0;
  if (!currentTime) return 0;
  return currentTime / duration;
}

function updateProgress(
  newProgress?: number,
  { duration = 0.2 }: { duration?: number } = {}
) {
  const audio = unref(audioRef);
  if (!audio) return 0;

  if (newProgress !== undefined) {
    audio.currentTime = audio.duration * newProgress;

    if (tl) {
      tl.progress(newProgress);
      updateScrollPosition();
    }

    updateSeekUI(newProgress, { duration });
  }

  return getProgress();
}

useEventListener(window, 'scrollsectionentered', () => {
  pause();
});

// Clean up ResizeObserver on unmount
onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<style>
.guided-voicemail_transcript {
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0,
    black var(--top-mask-size, 0),
    black calc(100% - var(--bottom-mask-size, 0)),
    transparent 100%
  );
  mask-image: linear-gradient(
    to bottom,
    transparent 0,
    black var(--top-mask-size, 0),
    black calc(100% - var(--bottom-mask-size, 0)),
    transparent 100%
  );
  --top-mask-size: 0px;
  --bottom-mask-size: 0px;
}

.fade-bottom {
  --bottom-mask-size: 64px !important;
}

.fade-top {
  --top-mask-size: 64px !important;
}

/* Custom audio player styles */
.guided-voicemail__progress-container {
  overflow: hidden;
  transition: height 0.15s ease;
  background-color: rgba(0, 0, 0, 0.2);
}

.dark .guided-voicemail__progress-container {
  background-color: rgba(0, 0, 0, 0.15);
}

.guided-voicemail__progress-fill {
  will-change: transform;
}

.guided-voicemail_audio button {
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

/* Forward/backward buttons - no background, inherit text color */
.guided-voicemail__control-btn {
  background-color: transparent;
  color: inherit;
}

.guided-voicemail__control-btn:hover {
  opacity: 0.7;
}

.guided-voicemail__control-btn:active {
  transform: scale(0.95);
  opacity: 0.5;
}

/* Play/pause button - distinct background */
.guided-voicemail__play-pause {
  background-color: #ffffff !important;
  color: #000000;
}

.dark .guided-voicemail__play-pause {
  background-color: #000000 !important;
  color: #ffffff;
}

.guided-voicemail__play-pause:hover {
  opacity: 0.9;
}

.guided-voicemail__play-pause:active {
  transform: scale(0.92);
  opacity: 0.8;
}

/* Hover effect for progress bar */
.guided-voicemail__progress-container:hover {
  height: 0.375rem;
}

/* Speed menu */
.guided-voicemail__speed-btn {
  min-width: 3rem;
}

.guided-voicemail__speed-menu {
  z-index: 50;
  min-width: 100px;
}

.guided-voicemail__speed-menu button:hover {
  opacity: 0.8;
}
</style>
